import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Flag } from 'lucide-react';
import { Button } from '@shared/ui/primitives/Button';
import { useBrandingStore } from '@shared/stores/brandingStore';
import { useBrandImagery } from '@shared/hooks/useBrandImagery';
import { interpolateCopy, pageCopy } from '@shared/lib/pageCopy';

/**
 * First-run welcome tour — a short photo-backed carousel shown straight
 * after a resident account is created, before they pick a residence.
 * Imagery is resolved from the tenant's domain config.
 */

export function WelcomeTour(): JSX.Element {
  const navigate = useNavigate();
  const branding = useBrandingStore((s) => s.branding);
  const { heroImage } = useBrandImagery();
  const name = branding?.name ?? 'IP4Estates';
  const slides = pageCopy().welcomeTour;
  const [index, setIndex] = useState(0);

  const slide = slides[index]!;
  const isLast = index === slides.length - 1;
  const finish = () => navigate('/select-property', { replace: true });

  return (
    <div className="relative flex min-h-full flex-col overflow-hidden bg-primary-900">
      <div
        aria-hidden
        className="absolute inset-0 bg-cover bg-center"
        style={heroImage ? { backgroundImage: `url("${heroImage}")` } : undefined}
      />
      <div
        aria-hidden
        className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/40 to-black/85"
      />

      <header className="relative z-10 flex items-center justify-between px-6 pt-safe-or-6">
        <span className="flex items-center gap-1.5 text-label uppercase tracking-[0.2em] text-white/90">
          <Flag className="h-4 w-4 text-brass" strokeWidth={1.75} />
          {name}
        </span>
        <button
          type="button"
          onClick={finish}
          className="text-body-sm font-medium text-white/70 hover:text-white"
        >
          Skip
        </button>
      </header>

      <div className="relative z-10 mt-auto flex flex-col gap-6 px-7 pb-safe-or-6 pb-8">
        <div>
          <span className="text-[0.6875rem] font-semibold uppercase tracking-[0.18em] text-brass">
            {slide.eyebrow}
          </span>
          <h1 className="display-serif mt-3 text-[2.3rem] leading-[1.08] text-white">
            {interpolateCopy(slide.title, { name })}
          </h1>
          <p className="mt-3 max-w-sm text-body text-white/75">{slide.body}</p>
        </div>

        <div className="flex gap-1.5" aria-hidden>
          {slides.map((_, i) => (
            <span
              key={i}
              className={
                'h-1.5 rounded-pill transition-all duration-normal ' +
                (i === index ? 'w-6 bg-brass' : 'w-1.5 bg-white/35')
              }
            />
          ))}
        </div>

        <Button
          variant="gold"
          size="lg"
          width="full"
          onClick={() => (isLast ? finish() : setIndex((i) => i + 1))}
        >
          {isLast ? 'Get started' : 'Continue'}
        </Button>
      </div>
    </div>
  );
}
